import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";

import { Button } from "primereact/button";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { TabView, TabPanel } from "primereact/tabview";

import { useAuth } from "context/AuthContext";
import { useLoading } from "context/LoadingContext";
import { useNotification } from "context/NotificationContext";

import Api from "utils/Api";
import MaskEnum from "utils/MaskEnum";
import MaskUtil from "utils/MaskUtil";
import { validarCPF } from "utils/ValidacaoUtils";

import Afastamento from "./modules/Afastamento";
import Alocacao from "./modules/Alocacao";
import Beneficios from "./modules/Beneficios";
import DadosAdmissionais from "./modules/DadosAdmissionais";
import DadosPessoais from "./modules/DadosPessoais";
import Dependentes from "./modules/Dependentes";
import Desligamento from "./modules/Desligamento";
import EscalaFuncionario from "./modules/EscalaFuncionario";
import ExameMedico from "./modules/ExameMedico";
import ListaDocumentos from "./modules/ListaDocumentos";
import Ocorrencia from "./modules/Ocorrencia";

const FormFuncionario = () => {
  const { setLoading } = useLoading();
  const auth = useAuth();
  const notify = useNotification();
  const Requicicao = new Api();
  const navigate = useNavigate();
  const { funcionarioId, empresaId } = useParams();

  const { TipoComissao } = MaskEnum;
  const optsStatus = MaskEnum.StatusCadastroFuncionari.map((status, index) => ({
    name: status,
    valor: index + 1
  }));

  const [activeIndex, setActiveIndex] = useState(0); // Gerencia a aba ativa
  const [dados, setDados] = useState({
    id: "",
    nome: "",
    cpf: "",
    rg: "",
    cnh: "",
    // Campos de endereço segregados
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    funcaoId: null,
    cargo: "", // Campo de cargo
    escolaridadeId: null,
    departamentoId: null,
    email: "",
    telefone: "",
    dataNascimento: "",
    dataAdmissao: "",
    banco: "",
    agencia: "",
    contaCorrente: "",
    dataAdmissaoExam: "",
    statusAptidao: null,
    nomeMedico: "",
    crm: "",
    tamanhoUniforme: "",
    pis: "",
    tituloEleitor: "",
    numeroCarteiraTrabalho: "",
    salario: "",
    horarioTrabalho: "",
    regimeContratacaoId: null, // Novo campo
    jornadaTrabalhoId: null, // Novo campo
    pcdId: null, // Novo campo
            tipoEscala: null, // Campo de tipo de escala
        isPcd: false, // Novo campo para indicar se é PCD

        urlLaudoMedico: null, // URL do laudo médico no GCP
        nomeArquivoLaudoMedico: null // Nome do arquivo do laudo médico
  });

  // Estado separado para o arquivo de laudo (seguindo padrão do Afastamento.jsx)
  const [laudoPcdFile, setLaudoPcdFile] = useState(null);

  const [funcoes, setFuncoes] = useState([]); // Usado para função e cargo
  const [escolaridades, setEscolaridades] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [regimes, setRegimes] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [pcds, setPcds] = useState([]);
  const [tiposParentesco, setTiposParentesco] = useState([]);
  const [ListaEmpresas, setListaEmpresas] = useState([]);

  const [legible, setLegible] = useState([]);

  const HandleChange = (event) => {
    const { name, value } = event.target;
    if (name === "banco")
      setDados((prevState) => ({
        ...prevState,
        [name]: MaskUtil.applyNumeroBancoMask(value)
      }));
    else if (name === "agencia")
      setDados((prevState) => ({
        ...prevState,
        [name]: MaskUtil.applyNumeroAgenciaMask(value)
      }));
    else if (name === "contaCorrente")
      setDados((prevState) => ({
        ...prevState,
        [name]: MaskUtil.applyNumeroContaMask(value)
      }));
    else setDados((prevState) => ({ ...prevState, [name]: value }));
  };



  const HandleDropdownChange = (e, field) => {
    setDados((prevState) => ({ ...prevState, [field]: e.value }));
  };

  const HandleSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);

      if (!dados.tipoEscala) {
        notify({
          type: "error",
          message: "O campo Tipo Escala é obrigatório. Por favor, preencha este campo na aba 'Dados Admissionais'."
        });
        return;
      }

      if (!dados.cpf || !dados.rg || !dados.telefone) {
        setLoading(false);
        notify({
          type: "aviso",
          message: "Os campos CPF, RG, Telefone são obrigatórios."
        });
        return;
      }

      if (!validarCPF(dados.cpf)) {
        setLoading(false);
        notify({
          type: "aviso",
          message: "CPF inválido. Verifique e tente novamente."
        });
        return;
      }



      const payload = {
        nome: dados.nome,
        cpf: dados.cpf, // Remove a formatação
        rg: dados.rg.replace(/\D/g, ""), // Remove a formatação
        cnh: dados.cnh.replace(/\D/g, ""), // Remove a formatação
        dataNascimento: dados.dataNascimento ? (() => {
          try {
            const [day, month, year] = dados.dataNascimento.split('/');
            // Cria a data e converte para ISO string
            const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
            return date.toISOString();
          } catch (error) {
            console.error("Erro ao formatar data para envio:", error);
            return null;
          }
        })() : null,
        // Campos de endereço segregados
        cep: dados.cep,
        logradouro: dados.logradouro,
        numero: dados.numero,
        complemento: dados.complemento,
        bairro: dados.bairro,
        cidade: dados.cidade,
        estado: dados.estado,
        email: dados.email,
        telefone: dados.telefone.replace(/\D/g, ""), // Remove a formatação
        funcaoId: dados.funcaoId,
        cargo: dados.cargo,
        departamentoId: dados.departamentoId,
        escolaridadeId: dados.escolaridadeId,
        dataAdmissao: dados.dataAdmissao ? (() => {
          try {
            const [day, month, year] = dados.dataAdmissao.split('/');
            // Cria a data e converte para ISO string
            const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
            return date.toISOString();
          } catch (error) {
            console.error("Erro ao formatar data de admissão para envio:", error);
            return null;
          }
        })() : null,
        banco: dados.banco,
        agencia: dados.agencia,
        contaCorrente: dados.contaCorrente,
        tamanhoUniforme: dados.tamanhoUniforme,
        salario:
          parseFloat(dados.salario.replace(/\./g, "").replace(",", ".")) || 0, // Converte para formato numérico
        horarioTrabalho: dados.horarioTrabalho,
        regimeContratacaoId: dados.regimeContratacaoId,
        jornadaTrabalhoId: dados.jornadaTrabalhoId,
        pcdId: dados.pcdId,
        statusCadastro: dados.statusCadastro?.valor ?? null,
        valeAlimentacao: dados.valeAlimentacao,
        valeRefeicao: dados.valeRefeicao,
        valeTransporte: dados.valeTransporte,
        planoSaude: dados.planoSaude,
        planoOdontologico: dados.planoOdontologico,
        seguroVida: dados.seguroVida,
        auxilioCreche: dados.auxilioCreche,
        valeAcademia: dados.valeAcademia,
        auxilioHomeOffice: dados.auxilioHomeOffice,
        tipoComissao: dados.tipoComissao,
        percentualComissao: dados.percentualComissao,
        comissaoFixa: dados.comissaoFixa,
        empresaId,
        pis: dados.pis,
        carteiraTrabalho: dados.carteiraTrabalho,
        tituloEleitor: dados.tituloEleitor,
        nomeMedico: dados.nomeMedico,
        crm: dados.crm,
        dataAdmissaoExam: dados.dataAdmissaoExam ? (() => {
          try {
            const [day, month, year] = dados.dataAdmissaoExam.split('/');
            // Cria a data e converte para ISO string
            const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
            return date.toISOString();
          } catch (error) {
            console.error("Erro ao formatar data do exame admissional para envio:", error);
            return null;
          }
        })() : null,
        statusAptidao: dados.statusAptidao,
        tipoEscala: dados.tipoEscala,
        ehPCD: dados.isPcd || false
      };

      let funcionarioIdCriado = funcionarioId;

      if (funcionarioId) {
        await Requicicao.Put({
          endpoint: "/Funcionarios/avaliacaoArquivos",
          data: legible,
          config: auth.GetHeaders()
        });

        const data = await Requicicao.Put({
          endpoint: `/Funcionarios/${funcionarioId}`,
          data: payload,
          config: auth.GetHeaders()
        });
        if (data.codigoEstadoHttp !== 200) {
          throw new Error(data.mensagemAdicional);
        }
      } else {
        const data = await Requicicao.Post({
          endpoint: "/Funcionarios",
          data: payload,
          config: auth.GetHeaders()
        });
        
        if (data.codigoEstadoHttp === 200) {
          funcionarioIdCriado = data.objetoResposta;
          notify({
            type: "success",
            message: "Funcionário cadastrado."
          });

          // Navega para a rota de edição com o ID do funcionário criado
          navigate(`/app/rh/funcionarios/editar/${funcionarioIdCriado}`, { replace: true });
        } else {
          throw new Error(data.mensagemAdicional || "Erro ao cadastrar funcionário");
        }
      }

      if (funcionarioId) {
        notify({
          type: "success",
          message: "Funcionário atualizado."
        });
      }

      // Se for PCD e tiver laudo para enviar, faz o upload
      if (dados.isPcd && laudoPcdFile && funcionarioIdCriado) {
        try {
          console.log("🔧 LaudoPcdFile:", laudoPcdFile);
          console.log("🔧 Tipo:", typeof laudoPcdFile);
          console.log("🔧 É File?", laudoPcdFile instanceof File);
          
          // Verificar se o arquivo ainda é um File válido
          if (!(laudoPcdFile instanceof File)) {
            console.error("❌ ERRO: laudoPcdFile não é um File object!");
            console.error("❌ Tipo recebido:", typeof laudoPcdFile);
            console.error("❌ Valor:", laudoPcdFile);
            notify({
              type: "error",
              message: "Erro interno: arquivo não é válido. Selecione o arquivo novamente."
            });
            return;
          }
          
          console.log("✅ Arquivo válido:", {
            name: laudoPcdFile.name,
            size: laudoPcdFile.size,
            type: laudoPcdFile.type
          });
          
          const formData = new FormData();
          formData.append("laudoMedico", laudoPcdFile); // Arquivo binário (mesmo padrão do Afastamento)
          
          // Debug FormData
          for (const [key, value] of formData.entries()) {
            console.log(`📤 FormData[${key}]:`, value);
            console.log(`📤 Tipo de ${key}:`, typeof value);
            console.log(`📤 É File ${key}?:`, value instanceof File);
          }

          // Usar o mesmo padrão do Afastamento.jsx
          const uploadResponse = await Requicicao.Put({
            endpoint: `/Funcionarios/${funcionarioIdCriado}/UploadLaudoMedico`,
            data: formData,
            config: auth.GetHeaders("multipart/form-data")
          });

          if (uploadResponse.codigoEstadoHttp === 200) {
            notify({
              type: "success",
              message: "Laudo médico enviado com sucesso!"
            });
          } else {
            console.error("Erro no upload:", uploadResponse);
            notify({
              type: "warning", 
              message: "Funcionário salvo, mas houve erro ao enviar o laudo médico."
            });
          }
        } catch (uploadError) {
          console.error("Erro ao enviar laudo médico:", uploadError);
          notify({
            type: "warning",
            message: "Funcionário salvo, mas houve erro ao enviar o laudo médico."
          });
        }
      }
    } catch (error) {
      console.error("Erro ao salvar o funcionário:", error);

      if (error.response && error.response.data) {
        const { mensagemAdicional, errors, status } = error.response.data;
        if (status === 400 && errors) {
          const errorMessages = Object.values(errors).flat().join(", ");
          notify({
            type: "error",
            message: `Erro ao salvar o funcionário: ${errorMessages}`
          });
        } else {
          notify({
            type: "error",
            message: `Erro ao salvar o funcionário: ${
              mensagemAdicional || "Erro desconhecido"
            }`
          });
        }
      } else {
        notify({
          type: "error",
          message: `Erro ao salvar o funcionário. ${error}`
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const NormalizacaoGuid = (valor) =>
      valor === "00000000-0000-0000-0000-000000000000" ? null : valor;
    const formatDate = (dateString) =>
      !dateString || dateString === "0001-01-01T00:00:00"
        ? null
        : new Date(dateString);

    const CarregarDadosFuncionario = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: `/Funcionarios/${funcionarioId}`,
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const data = response.objetoResposta;

          setDados((prevState) => ({
            ...prevState,
            ...data,
            statusCadastro: optsStatus.find(
              (obj) => obj.valor === data.statusCadastro
            ),
            funcaoId: NormalizacaoGuid(data.funcaoId),
            cargo: data.cargo,
            escolaridadeId: NormalizacaoGuid(data.escolaridadeId),
            departamentoId: NormalizacaoGuid(data.departamentoId),
            dataNascimento: (() => {
              if (!data.dataNascimento) return "";
              try {
                // Cria a data e ajusta para timezone local
                const date = new Date(data.dataNascimento);
                // Extrai apenas a data (ignora timezone)
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();
                // Formata para dd/MM/yyyy
                return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
              } catch (error) {
                console.error("Erro ao formatar data:", error);
                return "";
              }
            })(),
            dataAdmissao: (() => {
              if (!data.dataAdmissao) return "";
              try {
                // Cria a data e ajusta para timezone local
                const date = new Date(data.dataAdmissao);
                // Extrai apenas a data (ignora timezone)
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();
                // Formata para dd/MM/yyyy
                return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
              } catch (error) {
                console.error("Erro ao formatar data de admissão:", error);
                return "";
              }
            })(),
            dataAdmissaoExam: (() => {
              if (!data.dataAdmissaoExam) return "";
              try {
                // Cria a data e ajusta para timezone local
                const date = new Date(data.dataAdmissaoExam);
                // Extrai apenas a data (ignora timezone)
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();
                // Formata para dd/MM/yyyy
                return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
              } catch (error) {
                console.error("Erro ao formatar data do exame admissional:", error);
                return "";
              }
            })(),
            salario: data.salario
              ? data.salario.toFixed(2).replace(".", ",")
              : "",
            horarioTrabalho: data.horarioTrabalho,
            cpf: data.cpf,
            rg: MaskUtil.applyRgMask(data.rg),
            cnh: MaskUtil.applyCnhMask(data.cnh),
            telefone: MaskUtil.applyTelefoneMask(data.telefone),
            crm: MaskUtil.applyCrmMask(data.crm),
            regimeContratacaoId: data.regimeContratacaoId,
            jornadaTrabalhoId: data.jornadaTrabalhoId,
            pcdId: data.pcdId,
            isPcd: data.ehPCD || false,

            laudoPcd: null, // O arquivo não é carregado do backend, apenas as informações
            urlLaudoMedico: data.urlLaudoMedico || null,
            nomeArquivoLaudoMedico: data.nomeArquivoLaudoMedico || null
          }));
          

        }
      } catch (error) {
        console.error("Erro ao carregar os dados do funcionário:", error);
      }
    };

    const CarregarRegimes = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Regime",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedRegimes = response.objetoResposta
            .map((regime) => ({
              label: regime.descricao,
              value: regime.id
            }))
            .filter((x) => x.label !== "CLT Flex");
          setRegimes(formattedRegimes);
        }
      } catch (error) {
        console.error("Erro ao carregar regimes de contratação:", error);
      }
    };

    const CarregarJornadas = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Jornada",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedJornadas = response.objetoResposta
            .filter((x) => x.descricao !== "Escala 12x36")
            .map((jornada) => ({
              label: jornada.descricao,
              value: jornada.id
            }));
          setJornadas(formattedJornadas);
        }
      } catch (error) {
        console.error("Erro ao carregar jornadas de trabalho:", error);
      }
    };

    const CarregarPcds = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/PCD",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedPcds = response.objetoResposta.map((pcd) => ({
            label: pcd.descricao,
            value: pcd.id
          }));
          setPcds(formattedPcds);
        }
      } catch (error) {
        console.error("Erro ao carregar opções PCD:", error);
      }
    };

    const CarregarTiposParentesco = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Dependentes/TiposParentesco",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedTiposParentesco = response.objetoResposta.map((tipo) => ({
            label: tipo.descricao,
            value: tipo.id
          }));
          setTiposParentesco(formattedTiposParentesco);
        }
      } catch (error) {
        console.error("Erro ao carregar tipos de parentesco:", error);
      }
    };

    const CarregarFuncoes = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Funcoes",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedFuncoes = response.objetoResposta.map((funcao) => ({
            label: funcao.descricao,
            value: funcao.id
          }));
          setFuncoes(formattedFuncoes); // Usado para função e cargo
        }
      } catch (error) {
        console.error("Erro ao carregar funções:", error);
      }
    };

    const CarregarEscolaridades = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Escolaridades",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedEscolaridades = response.objetoResposta.map(
            (escolaridade) => ({
              label: escolaridade.descricao,
              value: escolaridade.id
            })
          );
          setEscolaridades(formattedEscolaridades);
        }
      } catch (error) {
        console.error("Erro ao carregar escolaridades:", error);
      }
    };

    const CarregarDepartamentos = async () => {
      try {
        const response = await Requicicao.Get({
          endpoint: "/Funcionarios/Departamentos",
          config: auth.GetHeaders()
        });
        if (response.codigoEstadoHttp === 200) {
          const formattedDepartamentos = response.objetoResposta.map(
            (departamento) => ({
              label: departamento.descricao,
              value: departamento.id
            })
          );
          setDepartamentos(formattedDepartamentos);
        }
      } catch (error) {
        console.error("Erro ao carregar departamentos:", error);
      }
    };

    const CarregarEmpresas = async () => {
      try {
        const data = await Requicicao.Get({
          endpoint: "/Funcionarios/ObterEmpresas",
          config: auth.GetHeaders()
        });
        if (data.codigoEstadoHttp === 200) {
          setListaEmpresas(data.objetoResposta);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const Go = async () => {
      try {
        setLoading(true);

        if (funcionarioId) {
          await CarregarDadosFuncionario();
        }
        await CarregarFuncoes();
        await CarregarEscolaridades();
        await CarregarDepartamentos();
        await CarregarRegimes();
        await CarregarJornadas();
        await CarregarPcds();
        await CarregarTiposParentesco();
        await CarregarEmpresas();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    Go();
  }, [funcionarioId]);

  const ValidarAcessoAba = (index) => {
    // Se é um novo funcionário (sem funcionarioId), só permite as primeiras 2 abas
    if (!funcionarioId) {
      // Abas permitidas para novos funcionários: 0 = Dados Pessoais, 1 = Dados Admissionais
      const abasPermitidas = [0, 1];
      
      if (!abasPermitidas.includes(index)) {
        notify({
          type: "aviso",
          message: "Você precisa salvar o funcionário primeiro para acessar outras abas."
        });
        return false;
      }
    }
    return true;
  };

  const HandleTabChange = (e) => {
    if (ValidarAcessoAba(e.index)) {
      setActiveIndex(e.index);
    }
  };

  const ConfirmacaoVoltar = (event) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Tem certeza que deseja voltar? Todos os dados não salvos serão perdidos.",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-warning",
      acceptLabel: "Sim, voltar",
      rejectLabel: "Cancelar",
      accept: () => navigate(-1),
      reject: () => {}
    });
  };

  const salvarAprovacoesDocumentos = async () => {
    try {
      const response = await Requicicao.Put({
        endpoint: "/Funcionarios/avaliacaoArquivos",
        data: legible,
        config: auth.GetHeaders()
      });

      if (response.codigoEstadoHttp !== 200) {
        throw new Error("Erro ao salvar aprovações dos documentos");
      }

      return true;
    } catch (error) {
      console.error("Erro ao salvar aprovações:", error);
      throw error; // Re-throw para ser tratado na função chamadora
    }
  };

  const handleStatusChange = async (newStatus, motivoReprovacao = "") => {
    try {
      setLoading(true);
      
      // Se for aprovação de documentos (status 8) ou reprovação (status 6), salva as avaliações primeiro
      if (newStatus === 8 || newStatus === 6) {
        try {
          // 1. Primeiro salva as aprovações/reprovações dos documentos
          await salvarAprovacoesDocumentos();
          
          // 2. Depois atualiza o status
          const payloadStatus = {
          nome: dados.nome,
          cpf: dados.cpf,
          rg: dados.rg,
          cnh: dados.cnh,
          dataNascimento: dados.dataNascimento ? (() => {
            try {
              const [day, month, year] = dados.dataNascimento.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data de nascimento:", error);
              return null;
            }
          })() : null,
          // Campos de endereço segregados
          cep: dados.cep,
          logradouro: dados.logradouro,
          numero: dados.numero,
          complemento: dados.complemento,
          bairro: dados.bairro,
          cidade: dados.cidade,
          estado: dados.estado,
          email: dados.email,
          telefone: dados.telefone,
          funcaoId: dados.funcaoId,
          cargo: dados.cargo,
          departamentoId: dados.departamentoId,
          escolaridadeId: dados.escolaridadeId,
          dataAdmissao: dados.dataAdmissao ? (() => {
            try {
              const [day, month, year] = dados.dataAdmissao.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data de admissão:", error);
              return null;
            }
          })() : null,
          banco: dados.banco,
          agencia: dados.agencia,
          contaCorrente: dados.contaCorrente,
          tamanhoUniforme: dados.tamanhoUniforme,
          salario: parseFloat(dados.salario?.replace(/\./g, "").replace(",", ".")) || 0,
          horarioTrabalho: dados.horarioTrabalho,
          regimeContratacaoId: dados.regimeContratacaoId,
          jornadaTrabalhoId: dados.jornadaTrabalhoId,
          pcdId: dados.pcdId,
          statusCadastro: newStatus,
          valeAlimentacao: dados.valeAlimentacao,
          valeRefeicao: dados.valeRefeicao,
          valeTransporte: dados.valeTransporte,
          planoSaude: dados.planoSaude,
          planoOdontologico: dados.planoOdontologico,
          seguroVida: dados.seguroVida,
          auxilioCreche: dados.auxilioCreche,
          valeAcademia: dados.valeAcademia,
          auxilioHomeOffice: dados.auxilioHomeOffice,
          tipoComissao: dados.tipoComissao,
          percentualComissao: dados.percentualComissao,
          comissaoFixa: dados.comissaoFixa,
          pis: dados.pis,
          carteiraTrabalho: dados.carteiraTrabalho,
          tituloEleitor: dados.tituloEleitor,
          nomeMedico: dados.nomeMedico,
          crm: dados.crm,
          dataAdmissaoExam: dados.dataAdmissaoExam ? (() => {
            try {
              const [day, month, year] = dados.dataAdmissaoExam.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data do exame admissional:", error);
              return null;
            }
          })() : null,
          statusAptidao: dados.statusAptidao,
          tipoEscala: dados.tipoEscala
        };

        const responseStatus = await Requicicao.Put({
          endpoint: `/Funcionarios/${funcionarioId}`,
          data: payloadStatus,
          config: auth.GetHeaders()
        });

          if (responseStatus.codigoEstadoHttp === 200) {
            // Atualiza o status local
            setDados(prev => ({
              ...prev,
              statusCadastro: optsStatus.find(s => s.valor === newStatus)
            }));

            notify({
              type: "success",
              message: newStatus === 8 
                ? "Documentos aprovados e processo concluído com sucesso!"
                : "Documentos reprovados e status atualizado com sucesso!"
            });
          } else {
            notify({
              type: "error",
              message: "Erro ao atualizar status. Tente novamente."
            });
          }
        } catch (error) {
          console.error("Erro ao processar avaliação:", error);
          notify({
            type: "error",
            message: newStatus === 8 
              ? "Erro ao processar aprovação dos documentos. Tente novamente."
              : "Erro ao processar reprovação dos documentos. Tente novamente."
          });
        }
      } else {
        // Para outros status, faz apenas a requisição de atualização de status
        const payload = {
          nome: dados.nome,
          cpf: dados.cpf,
          rg: dados.rg,
          cnh: dados.cnh,
          dataNascimento: dados.dataNascimento ? (() => {
            try {
              const [day, month, year] = dados.dataNascimento.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data de nascimento:", error);
              return null;
            }
          })() : null,
          // Campos de endereço segregados
          cep: dados.cep,
          logradouro: dados.logradouro,
          numero: dados.numero,
          complemento: dados.complemento,
          bairro: dados.bairro,
          cidade: dados.cidade,
          estado: dados.estado,
          email: dados.email,
          telefone: dados.telefone,
          funcaoId: dados.funcaoId,
          cargo: dados.cargo,
          departamentoId: dados.departamentoId,
          escolaridadeId: dados.escolaridadeId,
          dataAdmissao: dados.dataAdmissao ? (() => {
            try {
              const [day, month, year] = dados.dataAdmissao.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data de admissão:", error);
              return null;
            }
          })() : null,
          banco: dados.banco,
          agencia: dados.agencia,
          contaCorrente: dados.contaCorrente,
          tamanhoUniforme: dados.tamanhoUniforme,
          salario: parseFloat(dados.salario?.replace(/\./g, "").replace(",", ".")) || 0,
          horarioTrabalho: dados.horarioTrabalho,
          regimeContratacaoId: dados.regimeContratacaoId,
          jornadaTrabalhoId: dados.jornadaTrabalhoId,
          pcdId: dados.pcdId,
          statusCadastro: newStatus,
          valeAlimentacao: dados.valeAlimentacao,
          valeRefeicao: dados.valeRefeicao,
          valeTransporte: dados.valeTransporte,
          planoSaude: dados.planoSaude,
          planoOdontologico: dados.planoOdontologico,
          seguroVida: dados.seguroVida,
          auxilioCreche: dados.auxilioCreche,
          valeAcademia: dados.valeAcademia,
          auxilioHomeOffice: dados.auxilioHomeOffice,
          tipoComissao: dados.tipoComissao,
          percentualComissao: dados.percentualComissao,
          comissaoFixa: dados.comissaoFixa,
          pis: dados.pis,
          carteiraTrabalho: dados.carteiraTrabalho,
          tituloEleitor: dados.tituloEleitor,
          nomeMedico: dados.nomeMedico,
          crm: dados.crm,
          dataAdmissaoExam: dados.dataAdmissaoExam ? (() => {
            try {
              const [day, month, year] = dados.dataAdmissaoExam.split('/');
              const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
              return date.toISOString();
            } catch (error) {
              console.error("Erro ao formatar data do exame admissional:", error);
              return null;
            }
          })() : null,
          statusAptidao: dados.statusAptidao,
          tipoEscala: dados.tipoEscala
        };

        const response = await Requicicao.Put({
          endpoint: `/Funcionarios/${funcionarioId}`,
          data: payload,
          config: auth.GetHeaders()
        });

        if (response.codigoEstadoHttp === 200) {
          // Atualiza o status local
          setDados(prev => ({
            ...prev,
            statusCadastro: optsStatus.find(s => s.valor === newStatus)
          }));

          notify({
            type: "success",
            message: "Status atualizado com sucesso!"
          });
        } else {
          notify({
            type: "error",
            message: "Erro ao atualizar status. Tente novamente."
          });
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      notify({
        type: "error",
        message: "Erro ao atualizar status. Tente novamente."
      });
    } finally {
      setLoading(false);
    }
  };

  const BoxBtns = () => (
    <div className="d-flex justify-content-end">
      <Button
        type="button"
        label={funcionarioId ? "Atualizar" : "Salvar"}
        className="btn btn-primary"
        onClick={HandleSubmit}
      />
    </div>
  );

  const handleNextTab = () => {
    if (activeIndex < 1) { // Se está na primeira aba (Dados Pessoais), vai para a segunda (Dados Admissionais)
      setActiveIndex(1);
    } else if (activeIndex === 1) { // Se está na segunda aba (Dados Admissionais), vai para a terceira (Benefícios)
      setActiveIndex(2);
    }
  };

  const handlePreviousTab = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  return (
    <div className="card p-3" id="form-dados-funcionario">
      <ConfirmPopup />
      <style>
        {`
          .p-tabview .p-tabview-nav .p-tabview-item.disabled-tab {
            opacity: 0.6;
            pointer-events: none;
            cursor: not-allowed;
          }
          .p-tabview .p-tabview-nav .p-tabview-item.disabled-tab .p-tabview-nav-link {
            color: #dc3545 !important;
            background-color: #f8d7da !important;
            border-color: #f5c6cb !important;
          }
          .p-tabview .p-tabview-nav .p-tabview-item.disabled-tab .p-tabview-nav-link:hover {
            background-color: #f1b0b7 !important;
            border-color: #ed969e !important;
          }
          .p-tabview .p-tabview-nav .p-tabview-item.disabled-tab .p-tabview-nav-link:focus {
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
          }
        `}
      </style>
      <TabView
        activeIndex={activeIndex}
        onTabChange={HandleTabChange}
        renderActiveOnly={false}
        className="w-100"
      >
        <TabPanel header="Dados Pessoais">
          <DadosPessoais
            dados={dados}
            setDados={setDados}
            HandleChange={HandleChange}
            BoxBtns={BoxBtns}
            isNewFuncionario={!funcionarioId}
            onNextTab={handleNextTab}
            onSubmit={HandleSubmit}
          />
        </TabPanel>

        <TabPanel header="Dados Admissionais">
          <DadosAdmissionais
            dados={dados}
            setDados={setDados}
            funcoes={funcoes}
            HandleDropdownChange={HandleDropdownChange}
            HandleChange={HandleChange}
            departamentos={departamentos}
            regimes={regimes}
            jornadas={jornadas}
            pcds={pcds}
            escolaridades={escolaridades}
            BoxBtns={BoxBtns}
            isNewFuncionario={!funcionarioId}
            funcionarioId={funcionarioId}
            onNextTab={handleNextTab}
            onPreviousTab={handlePreviousTab}
            onSave={HandleSubmit}
            onLaudoPcdChange={setLaudoPcdFile}
          />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Benefícios" : "Benefícios"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Beneficios
            dados={dados}
            HandleChange={HandleChange}
            BoxBtns={BoxBtns}
          />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Comissões" : "Comissões"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="tipoComissao" className="form-label">
                Tipo de Comissão
              </label>
              <Dropdown
                id="tipoComissao"
                value={dados.tipoComissao}
                onChange={(e) => HandleDropdownChange(e, "tipoComissao")}
                options={TipoComissao}
                placeholder="Selecione uma Tipo"
                className="w-100"
              />
            </div>
            {dados.tipoComissao === 2 && (
              <div className="col-md-4">
                <label htmlFor="percentualComissao" className="form-label">
                  Percentual de Comissão
                </label>
                <InputNumber
                  id="percentualComissao"
                  className="w-100"
                  prefix="%"
                  max={100}
                  value={dados.percentualComissao}
                  onValueChange={(e) =>
                    HandleDropdownChange(e, "percentualComissao")
                  }
                />
              </div>
            )}
            {dados.tipoComissao === 1 && (
              <div className="col-md-4">
                <label htmlFor="comissaoFixa" className="form-label">
                  Comissão Fixa
                </label>
                <InputNumber
                  id="comissaoFixa"
                  className="w-100"
                  value={dados.comissaoFixa}
                  onValueChange={(e) => HandleDropdownChange(e, "comissaoFixa")}
                />
              </div>
            )}
            <div className="col-12">
              <BoxBtns />
            </div>
          </div>
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Exame Médico" : "Exame Médico"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <ExameMedico
            dados={dados}
            HandleChange={HandleChange}
            setDados={setDados}
            BoxBtns={BoxBtns}
            statusAptidao={dados.statusAptidao}
          />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Dependentes" : "Dependentes"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Dependentes dados={dados} tiposParentesco={tiposParentesco} />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Documentos" : "Documentos"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <ListaDocumentos
            dados={dados}
            funcionarioId={funcionarioId}
            legible={legible}
            setLegible={setLegible}
            onStatusChange={handleStatusChange}
          />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Escala" : "Escala"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <EscalaFuncionario id={funcionarioId} />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Afastamento" : "Afastamento"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Afastamento id={funcionarioId} />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Alocação" : "Alocação"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Alocacao
            id={funcionarioId}
            ListaEmpresas={ListaEmpresas}
            ListaFucao={funcoes}
          />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Ocorrência" : "Ocorrência"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Ocorrencia FuncionarioId={funcionarioId} />
        </TabPanel>

        <TabPanel 
          header={!funcionarioId ? "🔒 Desligamento" : "Desligamento"}
          className={!funcionarioId ? "disabled-tab" : ""}
        >
          <Desligamento id={funcionarioId} funcionario={dados} />
        </TabPanel>
      </TabView>
    </div>
  );
};

export default FormFuncionario;
